/**
 * RoleManager Component
 *
 * Admin component for managing roles with full CRUD functionality:
 * - Role list display with statistics
 * - Create new roles via RoleForm
 * - Edit and delete roles
 * - Role assignment management
 * - Skill association
 *
 * @module components/admin/RoleManager
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Shield,
  Plus,
  RefreshCw,
  Loader2,
  AlertCircle,
  Trash2,
  Edit,
  ChevronDown,
  ChevronUp,
  Users,
  Bot,
  Globe,
  FolderKanban,
  Search,
  Filter,
  X,
  Check,
  Settings,
  UserPlus,
  UserMinus,
  Save,
  Hash,
  FileText,
  Zap
} from 'lucide-react';

// ============================================
// Constants
// ============================================

const ROLE_SCOPE = {
  GLOBAL: 'global',
  PROJECT: 'project'
};

const ROLE_LEVEL = {
  EXECUTOR: 'executor',
  CONTRIBUTOR: 'contributor',
  LEAD: 'lead',
  OWNER: 'owner'
};

const LEVEL_CONFIG = {
  [ROLE_LEVEL.EXECUTOR]: {
    label: 'Executor',
    description: 'Führt definierte Tasks aus, fragt bei Unklarheiten nach',
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    border: 'border-gray-500/30',
    order: 1
  },
  [ROLE_LEVEL.CONTRIBUTOR]: {
    label: 'Contributor',
    description: 'Entwickelt eigenständig Lösungen im eigenen Bereich',
    bg: 'bg-green-500/20',
    text: 'text-green-400',
    border: 'border-green-500/30',
    order: 2
  },
  [ROLE_LEVEL.LEAD]: {
    label: 'Lead',
    description: 'Trifft technische Entscheidungen, koordiniert andere Rollen',
    bg: 'bg-purple-500/20',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
    order: 3
  },
  [ROLE_LEVEL.OWNER]: {
    label: 'Owner',
    description: 'Projekteigentümer, finale Entscheidungsgewalt',
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    order: 4
  }
};

const SCOPE_COLORS = {
  [ROLE_SCOPE.GLOBAL]: {
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
    icon: Globe
  },
  [ROLE_SCOPE.PROJECT]: {
    bg: 'bg-amber-500/20',
    text: 'text-amber-400',
    border: 'border-amber-500/30',
    icon: FolderKanban
  }
};

// ============================================
// API Functions
// ============================================

async function fetchRoles(options = {}) {
  const params = new URLSearchParams();
  if (options.scope) params.set('scope', options.scope);
  if (options.includeSystem !== undefined) params.set('includeSystem', String(options.includeSystem));
  if (options.page) params.set('page', options.page);
  if (options.limit) params.set('limit', options.limit);

  const response = await fetch(`/api/roles?${params}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch roles: ${response.statusText}`);
  }
  return response.json();
}

async function createRole(roleData) {
  const response = await fetch('/api/roles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to create role: ${response.statusText}`);
  }
  return response.json();
}

async function updateRole(id, roleData) {
  const response = await fetch(`/api/roles/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(roleData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to update role: ${response.statusText}`);
  }
  return response.json();
}

async function deleteRole(id) {
  const response = await fetch(`/api/roles/${id}`, {
    method: 'DELETE'
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to delete role: ${response.statusText}`);
  }
}

async function fetchRoleAssignments(roleId) {
  const response = await fetch(`/api/roles/${roleId}/assignments?limit=100`);
  if (!response.ok) {
    throw new Error(`Failed to fetch assignments: ${response.statusText}`);
  }
  return response.json();
}

async function assignRole(roleId, data) {
  const response = await fetch(`/api/roles/${roleId}/assign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to assign role: ${response.statusText}`);
  }
  return response.json();
}

async function revokeRole(roleId, data) {
  const response = await fetch(`/api/roles/${roleId}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to revoke role: ${response.statusText}`);
  }
  return response.json();
}

async function fetchUsers() {
  const response = await fetch('/api/users?limit=100');
  if (!response.ok) return [];
  const data = await response.json();
  return data.data || [];
}

async function fetchAgents() {
  const response = await fetch('/api/agents?limit=100');
  if (!response.ok) return [];
  const data = await response.json();
  return data.data || [];
}

async function fetchProjects() {
  const response = await fetch('/api/projects?limit=100');
  if (!response.ok) return [];
  const data = await response.json();
  return data.data || [];
}

async function fetchSkillsWithCategories() {
  // Fetch both skills and categories
  const [skillsRes, categoriesRes] = await Promise.all([
    fetch('/api/skills/db-skills', { credentials: 'include' }),
    fetch('/api/skills/categories', { credentials: 'include' })
  ]);

  if (!skillsRes.ok || !categoriesRes.ok) {
    throw new Error('Failed to fetch skills');
  }

  const skillsData = await skillsRes.json();
  const categoriesData = await categoriesRes.json();

  return {
    skills: skillsData.data || [],
    categories: categoriesData.data || []
  };
}

// ============================================
// Sub-Components
// ============================================

function ScopeBadge({ scope }) {
  const config = SCOPE_COLORS[scope] || SCOPE_COLORS[ROLE_SCOPE.GLOBAL];
  const Icon = config.icon;

  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3" />
      {scope === ROLE_SCOPE.GLOBAL ? 'Global' : 'Project'}
    </span>
  );
}

function SystemBadge() {
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-purple-500/20 text-purple-400">
      <Settings className="w-3 h-3" />
      System
    </span>
  );
}

function LevelBadge({ level }) {
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG[ROLE_LEVEL.CONTRIBUTOR];
  return (
    <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

/**
 * SkillMultiSelect Component
 * Multi-select dropdown for skills with category grouping
 */
function SkillMultiSelect({ selectedSkills, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [skillData, setSkillData] = useState({ skills: [], categories: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load skills when dropdown opens
  useEffect(() => {
    if (isOpen && skillData.skills.length === 0) {
      setIsLoading(true);
      fetchSkillsWithCategories()
        .then(setSkillData)
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, skillData.skills.length]);

  // Group skills by category
  const groupedSkills = useMemo(() => {
    const groups = new Map();

    // Create category groups
    for (const category of skillData.categories) {
      groups.set(category.id, {
        id: category.id,
        name: category.displayName || category.name,
        color: category.color,
        skills: []
      });
    }

    // Add uncategorized group
    groups.set('uncategorized', {
      id: 'uncategorized',
      name: 'Uncategorized',
      color: null,
      skills: []
    });

    // Distribute skills to groups
    for (const skill of skillData.skills) {
      // Only include skills and imported (not builtin)
      if (skill.class === 'builtin') continue;

      const groupId = skill.categoryId || 'uncategorized';
      const group = groups.get(groupId);
      if (group) {
        group.skills.push(skill);
      } else {
        groups.get('uncategorized').skills.push(skill);
      }
    }

    // Filter by search query and remove empty groups
    const result = [];
    for (const group of groups.values()) {
      const filteredSkills = group.skills.filter(s =>
        !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredSkills.length > 0) {
        result.push({ ...group, skills: filteredSkills });
      }
    }

    return result;
  }, [skillData, searchQuery]);

  const toggleSkill = (skillName) => {
    const newSelection = new Set(selectedSkills);
    if (newSelection.has(skillName)) {
      newSelection.delete(skillName);
    } else {
      newSelection.add(skillName);
    }
    onChange(Array.from(newSelection));
  };

  const toggleCategory = (categoryId) => {
    const group = groupedSkills.find(g => g.id === categoryId);
    if (!group) return;

    const categorySkillNames = group.skills.map(s => s.name);
    const allSelected = categorySkillNames.every(name => selectedSkills.includes(name));

    const newSelection = new Set(selectedSkills);
    if (allSelected) {
      // Deselect all in category
      categorySkillNames.forEach(name => newSelection.delete(name));
    } else {
      // Select all in category
      categorySkillNames.forEach(name => newSelection.add(name));
    }
    onChange(Array.from(newSelection));
  };

  const removeSkill = (skillName) => {
    onChange(selectedSkills.filter(s => s !== skillName));
  };

  const isCategoryFullySelected = (categoryId) => {
    const group = groupedSkills.find(g => g.id === categoryId);
    if (!group || group.skills.length === 0) return false;
    return group.skills.every(s => selectedSkills.includes(s.name));
  };

  const isCategoryPartiallySelected = (categoryId) => {
    const group = groupedSkills.find(g => g.id === categoryId);
    if (!group || group.skills.length === 0) return false;
    const selectedCount = group.skills.filter(s => selectedSkills.includes(s.name)).length;
    return selectedCount > 0 && selectedCount < group.skills.length;
  };

  return (
    <div className="relative">
      {/* Selected Skills Tags */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="
          min-h-[42px] px-3 py-2 rounded-md cursor-pointer
          bg-gray-900 border border-gray-700
          hover:border-gray-600 focus:outline-none
          flex flex-wrap gap-1.5 items-center
        "
      >
        {selectedSkills.length === 0 ? (
          <span className="text-gray-500 text-sm">Select skills...</span>
        ) : (
          selectedSkills.map(skillName => (
            <span
              key={skillName}
              className="
                flex items-center gap-1 px-2 py-0.5 rounded text-xs
                bg-indigo-500/20 text-indigo-300 border border-indigo-500/30
              "
            >
              {skillName}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSkill(skillName);
                }}
                className="hover:text-indigo-100"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
        <ChevronDown className={`w-4 h-4 text-gray-500 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="
          absolute z-50 mt-1 w-full max-h-80 overflow-auto
          bg-gray-800 border border-gray-700 rounded-lg shadow-xl
        ">
          {/* Search */}
          <div className="sticky top-0 bg-gray-800 p-2 border-b border-gray-700">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search skills..."
                className="
                  w-full pl-8 pr-3 py-1.5 text-sm rounded
                  bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
                  focus:outline-none focus:ring-1 focus:ring-indigo-500
                "
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
            </div>
          ) : groupedSkills.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm">
              No skills found
            </div>
          ) : (
            <div className="p-2 space-y-3">
              {groupedSkills.map((group) => (
                <div key={group.id}>
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => toggleCategory(group.id)}
                    className="
                      w-full flex items-center gap-2 px-2 py-1.5 rounded
                      hover:bg-gray-700/50 transition-colors text-left
                    "
                  >
                    <div className={`
                      w-4 h-4 rounded border flex items-center justify-center
                      ${isCategoryFullySelected(group.id)
                        ? 'bg-indigo-500 border-indigo-500'
                        : isCategoryPartiallySelected(group.id)
                          ? 'bg-indigo-500/50 border-indigo-500'
                          : 'border-gray-600'
                      }
                    `}>
                      {(isCategoryFullySelected(group.id) || isCategoryPartiallySelected(group.id)) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: group.color || '#6b7280' }}
                    />
                    <span className="text-sm font-medium text-gray-300">
                      {group.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({group.skills.filter(s => selectedSkills.includes(s.name)).length}/{group.skills.length})
                    </span>
                  </button>

                  {/* Skills in Category */}
                  <div className="ml-6 mt-1 space-y-0.5">
                    {group.skills.map((skill) => (
                      <button
                        key={skill.id}
                        type="button"
                        onClick={() => toggleSkill(skill.name)}
                        className="
                          w-full flex items-center gap-2 px-2 py-1 rounded
                          hover:bg-gray-700/50 transition-colors text-left
                        "
                      >
                        <div className={`
                          w-4 h-4 rounded border flex items-center justify-center
                          ${selectedSkills.includes(skill.name)
                            ? 'bg-indigo-500 border-indigo-500'
                            : 'border-gray-600'
                          }
                        `}>
                          {selectedSkills.includes(skill.name) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm text-gray-300">{skill.name}</span>
                        {skill.class === 'imported' && (
                          <span className="text-xs text-purple-400">(imported)</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Close button */}
          <div className="sticky bottom-0 bg-gray-800 p-2 border-t border-gray-700">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="w-full py-1.5 text-sm text-gray-400 hover:text-gray-300 rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RoleStats({ assignmentCount, permissionCount, skillCount }) {
  return (
    <div className="flex items-center gap-4 text-xs text-gray-500">
      <span className="flex items-center gap-1">
        <Users className="w-3.5 h-3.5" />
        {assignmentCount ?? 0} assignments
      </span>
      {permissionCount > 0 && (
        <span className="flex items-center gap-1">
          <Check className="w-3.5 h-3.5" />
          {permissionCount} permissions
        </span>
      )}
      {skillCount > 0 && (
        <span className="flex items-center gap-1">
          <Zap className="w-3.5 h-3.5" />
          {skillCount} skills
        </span>
      )}
    </div>
  );
}

function RoleItem({
  role,
  onEdit,
  onDelete,
  onViewAssignments,
  onAssign,
  isDeleting,
  isExpanded,
  onToggleExpand,
  assignments,
  assignmentsLoading,
  onRevokeAssignment,
  revokingId
}) {
  return (
    <div className={`bg-gray-800 border rounded-lg overflow-hidden transition-colors ${
      role.isSystem ? 'border-purple-500/30' : 'border-gray-700'
    }`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`p-2 rounded-lg ${SCOPE_COLORS[role.scope]?.bg || 'bg-gray-700'}`}>
              <Shield className={`w-5 h-5 ${SCOPE_COLORS[role.scope]?.text || 'text-gray-400'}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-medium text-white truncate">
                  {role.displayName || role.name}
                </h3>
                <span className="text-sm text-gray-500">@{role.name}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <ScopeBadge scope={role.scope} />
                <LevelBadge level={role.level} />
                {role.isSystem && <SystemBadge />}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!role.isSystem && (
              <button
                onClick={() => onAssign(role)}
                className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-green-400"
                title="Assign Role"
              >
                <UserPlus className="w-4 h-4" />
              </button>
            )}
            {!role.isSystem && (
              <button
                onClick={() => onEdit(role)}
                className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
            )}
            {!role.isSystem && (
              <button
                onClick={() => onDelete(role)}
                disabled={isDeleting}
                className="p-2 rounded hover:bg-gray-700 text-gray-400 hover:text-red-400 disabled:opacity-50"
                title="Delete"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            )}
            <button
              onClick={() => onToggleExpand(role.id)}
              className="p-2 rounded hover:bg-gray-700 text-gray-400"
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {role.description && (
          <p className="mt-2 text-sm text-gray-400 line-clamp-2">{role.description}</p>
        )}

        <div className="mt-3">
          <RoleStats
            assignmentCount={role.assignmentCount}
            permissionCount={role.permissions?.length || 0}
            skillCount={role.skills?.length || 0}
          />
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-gray-700 space-y-4">
          {/* Permissions */}
          {role.permissions?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Permissions</h4>
              <div className="flex flex-wrap gap-1">
                {role.permissions.map(perm => (
                  <span key={perm} className="px-2 py-0.5 rounded bg-gray-700 text-xs text-gray-300">
                    {perm}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {role.skills?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-300 mb-2">Skills</h4>
              <div className="flex flex-wrap gap-1">
                {role.skills.map(skill => (
                  <span key={skill} className="px-2 py-0.5 rounded bg-indigo-500/20 text-xs text-indigo-300">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Assignments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-300">Assignments</h4>
              {!role.isSystem && (
                <button
                  onClick={() => onAssign(role)}
                  className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
                >
                  <Plus className="w-3 h-3" />
                  Add
                </button>
              )}
            </div>

            {assignmentsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
              </div>
            ) : assignments.length === 0 ? (
              <p className="text-sm text-gray-500 py-2">No assignments yet</p>
            ) : (
              <div className="space-y-2">
                {assignments.map(assignment => (
                  <div
                    key={assignment.id}
                    className="flex items-center gap-3 p-2 rounded bg-gray-700/50"
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      assignment.type === 'user' ? 'bg-emerald-600' : 'bg-indigo-600'
                    }`}>
                      {assignment.type === 'user' ? (
                        <Users className="w-3 h-3 text-white" />
                      ) : (
                        <Bot className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">
                        {assignment.user?.displayName || assignment.user?.username ||
                         assignment.agent?.displayName || assignment.agent?.name}
                      </p>
                      {assignment.project && (
                        <p className="text-xs text-gray-500">
                          in {assignment.project.displayName || assignment.project.name}
                        </p>
                      )}
                    </div>
                    {!role.isSystem && (
                      <button
                        onClick={() => onRevokeAssignment(role, assignment)}
                        disabled={revokingId === assignment.id}
                        className="p-1 rounded hover:bg-gray-600 text-gray-400 hover:text-red-400 disabled:opacity-50"
                        title="Revoke"
                      >
                        {revokingId === assignment.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserMinus className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 pt-2 border-t border-gray-700/50">
            <div>
              <span className="text-gray-600">ID:</span>
              <span className="ml-2 font-mono text-xs">{role.id}</span>
            </div>
            <div>
              <span className="text-gray-600">Created:</span>
              <span className="ml-2">{new Date(role.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterDropdown({ value, onChange, options, icon: Icon }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="
          appearance-none pl-8 pr-8 py-2 rounded-md
          bg-gray-800 border border-gray-700 text-gray-300
          hover:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500
          text-sm cursor-pointer
        "
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
    </div>
  );
}

function RoleForm({ role, onSubmit, onCancel, isSubmitting }) {
  const [name, setName] = useState(role?.name || '');
  const [displayName, setDisplayName] = useState(role?.displayName || '');
  const [description, setDescription] = useState(role?.description || '');
  const [scope, setScope] = useState(role?.scope || 'global');
  const [level, setLevel] = useState(role?.level || 'contributor');
  const [permissions, setPermissions] = useState((role?.permissions || []).join(', '));
  const [skills, setSkills] = useState(role?.skills || []);

  const isEditing = !!role?.id;

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = {
      ...(isEditing ? {} : { name: name.trim() }),
      displayName: displayName.trim() || undefined,
      description: description.trim() || undefined,
      ...(isEditing ? {} : { scope }),
      level,
      permissions: permissions.split(',').map(p => p.trim()).filter(Boolean),
      skills: skills
    };
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-3 pb-4 border-b border-gray-700">
        <div className="p-2 rounded-lg bg-purple-500/10">
          <Shield className="w-6 h-6 text-purple-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-white">
            {isEditing ? 'Edit Role' : 'Create Role'}
          </h2>
          <p className="text-sm text-gray-400">
            {isEditing ? 'Update role configuration' : 'Create a new role with permissions'}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-200 mb-1.5">
            <Hash className="w-4 h-4 text-gray-400" />
            Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g., admin, developer, viewer"
            disabled={isEditing}
            required={!isEditing}
            className="
              w-full px-3 py-2 rounded-md
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          />
          <p className="mt-1 text-xs text-gray-500">Unique identifier (lowercase, no spaces)</p>
        </div>

        {/* Display Name */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-200 mb-1.5">
            <FileText className="w-4 h-4 text-gray-400" />
            Display Name
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g., Administrator, Developer"
            className="
              w-full px-3 py-2 rounded-md
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          />
        </div>

        {/* Description */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-200 mb-1.5">
            <FileText className="w-4 h-4 text-gray-400" />
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the role's purpose"
            rows={2}
            className="
              w-full px-3 py-2 rounded-md resize-none
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          />
        </div>

        {/* Scope */}
        {!isEditing && (
          <div>
            <label className="text-sm font-medium text-gray-200 mb-1.5 block">Scope</label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setScope('global')}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border
                  ${scope === 'global'
                    ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                  }
                `}
              >
                <Globe className="w-4 h-4" />
                Global
              </button>
              <button
                type="button"
                onClick={() => setScope('project')}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border
                  ${scope === 'project'
                    ? 'border-amber-500 bg-amber-500/10 text-amber-400'
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                  }
                `}
              >
                <FolderKanban className="w-4 h-4" />
                Project
              </button>
            </div>
          </div>
        )}

        {/* Level */}
        <div>
          <label className="text-sm font-medium text-gray-200 mb-1.5 block">Hierarchy Level</label>
          <div className="flex gap-2">
            {Object.entries(LEVEL_CONFIG).map(([key, config]) => (
              <button
                key={key}
                type="button"
                onClick={() => setLevel(key)}
                className={`
                  flex-1 flex flex-col items-center gap-1 px-3 py-2 rounded-md border text-center
                  ${level === key
                    ? `${config.border} ${config.bg} ${config.text}`
                    : 'border-gray-700 bg-gray-800 text-gray-400 hover:border-gray-600'
                  }
                `}
              >
                <span className="font-medium text-sm">{config.label}</span>
                <span className="text-xs opacity-75">{config.description.split(',')[0]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-200 mb-1.5">
            <Check className="w-4 h-4 text-gray-400" />
            Permissions
          </label>
          <input
            type="text"
            value={permissions}
            onChange={(e) => setPermissions(e.target.value)}
            placeholder="admin, write, read (comma-separated)"
            className="
              w-full px-3 py-2 rounded-md
              bg-gray-900 border border-gray-700 text-gray-100 placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          />
          <p className="mt-1 text-xs text-gray-500">Comma-separated list of permissions</p>
        </div>

        {/* Skills */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-200 mb-1.5">
            <Zap className="w-4 h-4 text-gray-400" />
            Skills
          </label>
          <SkillMultiSelect
            selectedSkills={skills}
            onChange={setSkills}
          />
          <p className="mt-1 text-xs text-gray-500">
            Click a category to select all its skills, or select individual skills
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting || (!isEditing && !name.trim())}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditing ? 'Save Changes' : 'Create Role'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function AssignmentModal({ role, users, agents, projects, onSubmit, onCancel, isSubmitting }) {
  const [type, setType] = useState('user');
  const [entityId, setEntityId] = useState('');
  const [projectId, setProjectId] = useState('');

  const entities = type === 'user' ? users : agents;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!entityId) return;

    const data = {
      ...(type === 'user' ? { userId: entityId } : { agentId: entityId }),
      ...(projectId ? { projectId } : {})
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-green-400" />
            <div>
              <h3 className="font-medium text-white">Assign Role</h3>
              <p className="text-sm text-gray-400">{role.displayName || role.name}</p>
            </div>
          </div>
          <button onClick={onCancel} className="p-1 rounded hover:bg-gray-700 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type selection */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setType('user'); setEntityId(''); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border ${
                type === 'user'
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400'
                  : 'border-gray-700 bg-gray-900 text-gray-400'
              }`}
            >
              <Users className="w-4 h-4" />
              User
            </button>
            <button
              type="button"
              onClick={() => { setType('agent'); setEntityId(''); }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border ${
                type === 'agent'
                  ? 'border-indigo-500 bg-indigo-500/10 text-indigo-400'
                  : 'border-gray-700 bg-gray-900 text-gray-400'
              }`}
            >
              <Bot className="w-4 h-4" />
              Agent
            </button>
          </div>

          {/* Entity selection */}
          <div>
            <label className="text-sm font-medium text-gray-200 mb-1.5 block">
              Select {type === 'user' ? 'User' : 'Agent'}
            </label>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              required
              className="
                w-full px-3 py-2 rounded-md appearance-none
                bg-gray-900 border border-gray-700 text-gray-100
                focus:outline-none focus:ring-2 focus:ring-indigo-500
              "
            >
              <option value="">Select...</option>
              {entities.map(entity => (
                <option key={entity.id} value={entity.id}>
                  {entity.displayName || entity.name || entity.username}
                </option>
              ))}
            </select>
          </div>

          {/* Project selection (for project-scoped roles or optional) */}
          {(role.scope === 'project' || projects.length > 0) && (
            <div>
              <label className="text-sm font-medium text-gray-200 mb-1.5 block">
                Project {role.scope === 'project' && <span className="text-red-400">*</span>}
              </label>
              <select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                required={role.scope === 'project'}
                className="
                  w-full px-3 py-2 rounded-md appearance-none
                  bg-gray-900 border border-gray-700 text-gray-100
                  focus:outline-none focus:ring-2 focus:ring-indigo-500
                "
              >
                <option value="">{role.scope === 'project' ? 'Select project...' : 'Global (no project)'}</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.displayName || project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !entityId}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 hover:bg-green-500 text-white disabled:opacity-50"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4" />
              )}
              Assign
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ role, onConfirm, onCancel, isDeleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-red-500/10">
            <AlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-white">Delete Role</h3>
        </div>

        <p className="text-gray-400 mb-2">
          Are you sure you want to delete <strong className="text-white">{role.displayName || role.name}</strong>?
        </p>
        <p className="text-sm text-gray-500 mb-6">
          This will remove all role assignments. This action cannot be undone.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4" />
                Delete Role
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function RoleManager() {
  // State
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Expansion and assignments state
  const [expandedId, setExpandedId] = useState(null);
  const [roleAssignments, setRoleAssignments] = useState({});
  const [assignmentsLoading, setAssignmentsLoading] = useState({});
  const [revokingId, setRevokingId] = useState(null);

  // Assignment modal state
  const [assignModalRole, setAssignModalRole] = useState(null);
  const [assignSubmitting, setAssignSubmitting] = useState(false);

  // Resources for assignment
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [projects, setProjects] = useState([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [showSystem, setShowSystem] = useState(true);

  const scopeFilterOptions = [
    { value: 'all', label: 'All Scopes' },
    { value: 'global', label: 'Global' },
    { value: 'project', label: 'Project' }
  ];

  // Load roles
  const loadRoles = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchRoles({
        scope: scopeFilter !== 'all' ? scopeFilter : undefined,
        includeSystem: showSystem,
        limit: 100
      });
      setRoles(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [scopeFilter, showSystem]);

  // Load resources for assignment modal
  const loadAssignmentResources = useCallback(async () => {
    try {
      const [usersData, agentsData, projectsData] = await Promise.all([
        fetchUsers(),
        fetchAgents(),
        fetchProjects()
      ]);
      setUsers(usersData);
      setAgents(agentsData);
      setProjects(projectsData);
    } catch (err) {
      console.error('Failed to load resources:', err);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    loadAssignmentResources();
  }, [loadAssignmentResources]);

  // Filtered roles
  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    const query = searchQuery.toLowerCase();
    return roles.filter(r =>
      r.name.toLowerCase().includes(query) ||
      r.displayName?.toLowerCase().includes(query) ||
      r.description?.toLowerCase().includes(query)
    );
  }, [roles, searchQuery]);

  // Handlers
  const handleToggleExpand = useCallback(async (roleId) => {
    if (expandedId === roleId) {
      setExpandedId(null);
      return;
    }

    setExpandedId(roleId);

    // Fetch assignments if not cached
    if (!roleAssignments[roleId]) {
      setAssignmentsLoading(prev => ({ ...prev, [roleId]: true }));
      try {
        const result = await fetchRoleAssignments(roleId);
        setRoleAssignments(prev => ({ ...prev, [roleId]: result.data || [] }));
      } catch (err) {
        console.error('Failed to load assignments:', err);
        setRoleAssignments(prev => ({ ...prev, [roleId]: [] }));
      } finally {
        setAssignmentsLoading(prev => ({ ...prev, [roleId]: false }));
      }
    }
  }, [expandedId, roleAssignments]);

  const handleSubmit = useCallback(async (formData) => {
    setIsSubmitting(true);
    try {
      if (editingRole) {
        await updateRole(editingRole.id, formData);
      } else {
        await createRole(formData);
      }
      await loadRoles();
      setShowForm(false);
      setEditingRole(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setIsSubmitting(false);
    }
  }, [editingRole, loadRoles]);

  const handleEdit = useCallback((role) => {
    setEditingRole(role);
    setShowForm(true);
  }, []);

  const handleDeleteClick = useCallback((role) => {
    setRoleToDelete(role);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!roleToDelete) return;
    setDeletingId(roleToDelete.id);
    try {
      await deleteRole(roleToDelete.id);
      await loadRoles();
      setRoleToDelete(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  }, [roleToDelete, loadRoles]);

  const handleOpenAssignModal = useCallback((role) => {
    setAssignModalRole(role);
  }, []);

  const handleAssign = useCallback(async (data) => {
    if (!assignModalRole) return;
    setAssignSubmitting(true);
    try {
      await assignRole(assignModalRole.id, data);
      // Refresh assignments for this role
      const result = await fetchRoleAssignments(assignModalRole.id);
      setRoleAssignments(prev => ({ ...prev, [assignModalRole.id]: result.data || [] }));
      await loadRoles(); // Refresh counts
      setAssignModalRole(null);
    } catch (err) {
      alert(err.message);
    } finally {
      setAssignSubmitting(false);
    }
  }, [assignModalRole, loadRoles]);

  const handleRevokeAssignment = useCallback(async (role, assignment) => {
    setRevokingId(assignment.id);
    try {
      const data = {
        ...(assignment.user ? { userId: assignment.user.id } : {}),
        ...(assignment.agent ? { agentId: assignment.agent.id } : {}),
        ...(assignment.project ? { projectId: assignment.project.id } : {})
      };
      await revokeRole(role.id, data);
      // Update local state
      setRoleAssignments(prev => ({
        ...prev,
        [role.id]: (prev[role.id] || []).filter(a => a.id !== assignment.id)
      }));
      await loadRoles(); // Refresh counts
    } catch (err) {
      alert(err.message);
    } finally {
      setRevokingId(null);
    }
  }, [loadRoles]);

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setEditingRole(null);
  }, []);

  // Render
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Shield className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">Role Management</h1>
            <p className="text-sm text-gray-400">Create and manage roles with permissions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadRoles}
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => { setEditingRole(null); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            <Plus className="w-4 h-4" />
            Create Role
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search roles..."
            className="w-full pl-10 pr-4 py-2 rounded-md bg-gray-800 border border-gray-700 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-400"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <FilterDropdown
          value={scopeFilter}
          onChange={setScopeFilter}
          options={scopeFilterOptions}
          icon={Filter}
        />

        <label className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 cursor-pointer hover:border-gray-600">
          <input
            type="checkbox"
            checked={showSystem}
            onChange={(e) => setShowSystem(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-gray-800"
          />
          <Settings className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-400">Show system roles</span>
        </label>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-red-500/10 border border-red-500/30 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <RoleForm
            role={editingRole}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
          />
        </div>
      )}

      {/* Loading */}
      {isLoading && !roles.length && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !roles.length && !showForm && (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-300">No roles found</h3>
          <p className="text-gray-500 mt-1">Create a role to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white"
          >
            Create Your First Role
          </button>
        </div>
      )}

      {/* Role list */}
      {filteredRoles.length > 0 && (
        <div className="space-y-3">
          <div className="text-sm text-gray-500">
            {filteredRoles.length} role{filteredRoles.length !== 1 ? 's' : ''}
            {searchQuery && ` matching "${searchQuery}"`}
          </div>

          {filteredRoles.map((role) => (
            <RoleItem
              key={role.id}
              role={role}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onAssign={handleOpenAssignModal}
              onViewAssignments={() => handleToggleExpand(role.id)}
              isDeleting={deletingId === role.id}
              isExpanded={expandedId === role.id}
              onToggleExpand={handleToggleExpand}
              assignments={roleAssignments[role.id] || []}
              assignmentsLoading={assignmentsLoading[role.id]}
              onRevokeAssignment={handleRevokeAssignment}
              revokingId={revokingId}
            />
          ))}
        </div>
      )}

      {/* Assignment Modal */}
      {assignModalRole && (
        <AssignmentModal
          role={assignModalRole}
          users={users}
          agents={agents}
          projects={projects}
          onSubmit={handleAssign}
          onCancel={() => setAssignModalRole(null)}
          isSubmitting={assignSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {roleToDelete && (
        <DeleteConfirmModal
          role={roleToDelete}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setRoleToDelete(null)}
          isDeleting={deletingId === roleToDelete.id}
        />
      )}
    </div>
  );
}
